import { CSSProperties } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// ProgressBar — slim animated progress bar with determinate + indeterminate
// variants. Used wherever a non-blocking "we're working" signal is needed.
//
// Determinate (value 0..100): the fill grows from the left.
// Indeterminate (no value or `indeterminate` prop): a thin gradient slug
// slides across the track on a loop — same pattern main uses for in-flight
// table refreshes.
//
// Two surface modes:
//   • 'bar' (default) — the standalone bar. Drop it inside a flex/grid cell.
//   • 'border-top' — absolutely-positioned strip pinned to the top edge of a
//     parent container. Useful when you want the bar to glue to a table or
//     panel without affecting its layout. The parent must have
//     `position: relative` and `overflow: hidden`.
// ─────────────────────────────────────────────────────────────────────────────

export interface ProgressBarProps {
    /** 0..100. Omit when indeterminate. */
    value?: number
    /** Force indeterminate mode regardless of value. */
    indeterminate?: boolean
    /** Track tone — blue (default) maps to the scanning/agent state. */
    tone?: 'accent' | 'success' | 'warning' | 'danger' | 'neutral'
    /** Height in px. Defaults to 2px. */
    height?: number
    /** 'bar' renders a normal in-flow bar. 'border-top' pins it to the parent's
     *  top edge as a slim border. */
    variant?: 'bar' | 'border-top'
    className?: string
    'aria-label'?: string
}

const trackToneClasses: Record<NonNullable<ProgressBarProps['tone']>, string> = {
    accent: 'bg-blue-100 dark:bg-blue-900/40',
    success: 'bg-green-100 dark:bg-green-900/40',
    warning: 'bg-yellow-100 dark:bg-yellow-900/40',
    danger: 'bg-red-100 dark:bg-red-900/40',
    neutral: 'bg-slate-200 dark:bg-slate-700',
}

const fillToneClasses: Record<NonNullable<ProgressBarProps['tone']>, string> = {
    accent: 'bg-blue-600 dark:bg-blue-400',
    success: 'bg-green-600 dark:bg-green-400',
    warning: 'bg-yellow-500 dark:bg-yellow-400',
    danger: 'bg-red-600 dark:bg-red-400',
    neutral: 'bg-slate-500 dark:bg-slate-400',
}

export const ProgressBar = ({
    value,
    indeterminate,
    tone = 'accent',
    height = 2,
    variant = 'bar',
    className = '',
    'aria-label': ariaLabel,
}: ProgressBarProps) => {
    const isIndeterminate = indeterminate || value === undefined
    const clampedValue = value !== undefined ? Math.max(0, Math.min(100, value)) : 0

    const trackBaseClasses =
        variant === 'border-top'
            ? `pointer-events-none absolute top-0 left-0 right-0 z-10 overflow-hidden ${trackToneClasses[tone]}`
            : `relative w-full overflow-hidden rounded-full ${trackToneClasses[tone]}`

    const trackStyle: CSSProperties = { height }

    return (
        <div
            role="progressbar"
            aria-label={ariaLabel ?? (isIndeterminate ? 'Loading' : `${Math.round(clampedValue)}% complete`)}
            aria-valuemin={isIndeterminate ? undefined : 0}
            aria-valuemax={isIndeterminate ? undefined : 100}
            aria-valuenow={isIndeterminate ? undefined : Math.round(clampedValue)}
            className={`${trackBaseClasses} ${className}`}
            style={trackStyle}
        >
            {isIndeterminate ? (
                // Two overlapping gradient slugs that slide across the track at
                // different speeds, matching main's `slideRight` keyframe rhythm.
                // Pure CSS — no JS timer.
                <div className={`absolute inset-y-0 ${fillToneClasses[tone]} ds-progress-indeterminate rounded-full`} />
            ) : (
                <div
                    className={`h-full ${fillToneClasses[tone]} rounded-full transition-[width] duration-300 ease-out`}
                    style={{ width: `${clampedValue}%` }}
                />
            )}
        </div>
    )
}
